import type { ReactNode } from 'react';
import * as RT from '@radix-ui/react-tabs';
import { cn } from './cn';
import { Tooltip } from './Tooltip';

export interface TabItem {
  key: string;
  label: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  /**
   * Why a DISABLED tab cannot be opened yet ("Opens once the meet starts"), shown on hover. A greyed
   * tab with no reason reads as broken or as a missing permission.
   */
  hint?: ReactNode;
}

/** Ant Design `Tabs` (items API) replacement, built on Radix Tabs. */
export function Tabs({
  items, activeKey, defaultActiveKey, onChange, tabBarExtraContent, className, size = 'middle',
}: {
  items: TabItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  tabBarExtraContent?: ReactNode;
  className?: string;
  size?: 'small' | 'middle' | 'large';
}) {
  const pad = size === 'small' ? 'px-2.5 py-2 text-sm' : size === 'large' ? 'px-4 py-3 text-[15px]' : 'px-3 py-2.5 text-sm';
  return (
    <RT.Root
      value={activeKey}
      defaultValue={defaultActiveKey ?? items[0]?.key}
      onValueChange={onChange}
      className={className}
    >
      <div className="flex flex-wrap-reverse items-center justify-between gap-x-3 gap-y-2 border-b border-line">
        <RT.List className="-mb-px flex max-w-full gap-1 overflow-x-auto">
          {items.map((it) => {
            const trigger = (
            <RT.Trigger
              key={it.key}
              value={it.key}
              disabled={it.disabled}
              className={cn(
                // A disabled button swallows the hover, so it lets the pointer through to the hint's span.
                it.disabled && it.hint && 'disabled:pointer-events-none',
                // Hover tints the ink brand-green rather than painting a background — same cue the
                // Segmented pill uses, so every tab-style control in the app reads the same on hover.
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent font-medium text-muted transition-colors hover:text-brand-700 dark:hover:text-brand-300',
                'data-[state=active]:border-brand-600 data-[state=active]:text-brand-700 dark:data-[state=active]:text-brand-300',
                'disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:text-content [&>svg]:size-4',
                pad,
              )}
            >
              {it.icon}
              {it.label}
            </RT.Trigger>
            );
            return it.disabled && it.hint
              ? <Tooltip key={it.key} label={it.hint}><span className="inline-flex shrink-0 cursor-not-allowed">{trigger}</span></Tooltip>
              : trigger;
          })}
        </RT.List>
        {tabBarExtraContent && <div className="ml-auto max-w-full shrink-0 pb-1.5">{tabBarExtraContent}</div>}
      </div>
      {items.map((it) => (
        <RT.Content key={it.key} value={it.key} className="pt-4 focus-visible:outline-none">
          {it.children}
        </RT.Content>
      ))}
    </RT.Root>
  );
}
